/**
 * S-27 «Калькулятор прибыли» — по каждой модели, как в кабинете WB (просьба заказчика 21.10.2026).
 *
 * Поля WB (цена, скидка, комиссия, эквайринг, логистика, хранение, приёмка) подставлены из отчётов
 * WB о реализации за 4 недели — их можно поправить; себестоимость, упаковку, доставку, рекламу,
 * прочие расходы и НДС вводит владелец. Итог — прибыль с одной проданной штуки — считается сразу,
 * той же формулой, что на сервере (@wb/contracts/profit), и после сохранения по нему считается
 * прибыль недели: проданные штуки × прибыль со штуки.
 *
 * Налог на доход здесь не считается (решение заказчика 22.10.2026: «его сами решим»), только НДС
 * к уплате: НДС с продажи минус НДС в себестоимости, в услугах WB (без эквайринга) и в рекламе.
 */
import { useEffect, useMemo, useState } from 'react'
import { computeProfit, checkProfitInputs, type ProfitInputs, type TaxInputs } from '@wb/contracts/profit'
import { fetchCalc, saveCalc, deleteCalc, type CalcData } from '../api/sections.js'
import { confirmAction, hapticSuccess } from '../telegram/call.js'
import { capitalize, dayMonth } from './words.js'
import { useTwoCols } from '../wide.js'
import type { ScreenId } from '../router/screens.js'
import { Icon } from '../components/icons/Icon.js'
import { Failed, InlineError, Loading, NoShop, useAnnounce, useData, useScreenHead } from './common.js'
import { Sect } from './ui.js'

type Open = (id: ScreenId, param?: string) => void
type Key = Exclude<keyof ProfitInputs, 'cogs_vat_rub'>
type Form = Record<Key, string>

const KEYS: Key[] = ['price_before_discount', 'seller_discount_pct', 'commission_pct', 'acquiring_pct', 'logistics_rub', 'storage_rub',
  'acceptance_rub', 'cogs_rub', 'packaging_rub', 'inbound_rub', 'ads_pct', 'other_rub', 'spp_pct']
const WB_KEYS: Key[] = ['price_before_discount', 'seller_discount_pct', 'commission_pct', 'acquiring_pct', 'logistics_rub', 'storage_rub', 'acceptance_rub', 'spp_pct']

const MINUS = '−'
const str = (n: number): string => (Number.isFinite(n) ? String(Math.round(n * 100) / 100).replace('.', ',') : '')
const parse = (s: string): number => (s.trim() === '' ? 0 : Number(s.replace(/\s/g, '').replace(',', '.')))
const rub = (n: number): string => {
  const r = Math.round(n * 100) / 100
  const t = Math.abs(r).toLocaleString('ru-RU', { minimumFractionDigits: Number.isInteger(r) ? 0 : 2, maximumFractionDigits: 2 })
  return (r < 0 ? MINUS : '') + t + ' ₽'
}
const pct = (n: number | null): string => (n === null ? '—' : (n < 0 ? MINUS : '') + Math.abs(n).toLocaleString('ru-RU', { maximumFractionDigits: 1 }) + ' %')
const toForm = (i: ProfitInputs): Form => Object.fromEntries(KEYS.map((k) => [k, str(i[k])])) as Form

function Num({ id, label, unit, value, onChange, hint, disabled }: {
  id: string; label: string; unit: '₽' | '%'; value: string; onChange: (v: string) => void; hint?: string | undefined; disabled: boolean
}): React.JSX.Element {
  const bad = value.trim() !== '' && !Number.isFinite(parse(value))
  return (
    <div className="calc-field">
      <label htmlFor={id} className="calc-label">{label}{hint !== undefined && <span className="row-meta calc-hint">{hint}</span>}</label>
      <span className="calc-inp">
        <input id={id} className={'inp' + (bad ? ' inp-bad' : '')} inputMode="decimal" autoComplete="off" value={value} disabled={disabled}
          data-testid={id} aria-invalid={bad} onChange={(e) => onChange(e.target.value)} />
        <span className="calc-unit" aria-hidden="true">{unit}</span>
      </span>
    </div>
  )
}

export function ScreenCalc({ nm, replace, open }: { nm: string; replace: Open; open: Open }): React.JSX.Element {
  // Какой расчёт открыт: null — действующий сегодня; дата — расчёт, начинающий действовать с неё.
  const [pick, setPick] = useState<{ nm: string; at: string } | null>(null)
  const at = pick !== null && pick.nm === nm ? pick.at : null
  const { s, reload } = useData(() => fetchCalc(nm, at), [nm, at])
  const d = s.kind === 'ok' && s.data.shop !== null ? s.data : null
  useScreenHead('Калькулятор прибыли', d === null ? null : d.position)
  if (s.kind === 'loading') return <Loading label="Загрузка калькулятора" rows={8} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  if (d === null) return <NoShop reason={s.data.reason} />
  const onPick = (v: string | null): void => { if (v === at) reload(); else setPick(v === null ? null : { nm, at: v }) }
  return <CalcForm key={`${d.nm_id}:${d.saved?.valid_from ?? ''}`} d={d} replace={replace} open={open} onPick={onPick} />
}

const plusDays = (ymd: string, n: number): string => new Date(Date.parse(ymd + 'T00:00:00Z') + n * 86_400_000).toISOString().slice(0, 10)

function CalcForm({ d, replace, open, onPick }: { d: CalcData; replace: Open; open: Open; onPick: (at: string | null) => void }): React.JSX.Element {
  const announce = useAnnounce()
  const two = useTwoCols()
  const [f, setF] = useState<Form>(() => toForm(d.saved?.inputs ?? d.prefill.inputs))
  const [vat, setVat] = useState(() => String(d.tax.vat_pct))
  // С какой даты действует (заказчик 21.10.2026): новая партия — новый расчёт с даты её прихода, прежние
  // продажи остаются по прежнему; «исправить» — правка открытого расчёта на его же даты.
  const [mode, setMode] = useState<'new' | 'fix'>(() => (d.saved !== null && d.saved.state !== 'current' ? 'fix' : 'new'))
  const [from, setFrom] = useState(d.today)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [dirty, setDirty] = useState(d.saved === null)
  useEffect(() => { setErr(null) }, [f, vat, mode, from])

  // НДС в себестоимости — 22/122 от неё (заказчик 21.10.2026: ввозной НДС отдельно не вводится).
  const inputs = useMemo(() => ({ ...Object.fromEntries(KEYS.map((k) => [k, parse(f[k])])), cogs_vat_rub: null }) as unknown as ProfitInputs, [f])
  const taxIn: TaxInputs = { regime: 'none', rate_pct: 0, vat_pct: parse(vat), base: 'price' }
  const problems = checkProfitInputs(inputs, taxIn)
  const r = computeProfit(inputs, taxIn)
  const vatN = parse(vat)
  const set = (k: Key) => (v: string): void => { setF({ ...f, [k]: v }); setDirty(true) }
  const src = (k: Key): string | undefined => d.prefill.source[k]
  const ro = !d.can_edit

  const validFrom = d.saved === null ? null : mode === 'fix' ? d.saved.valid_from : from
  const clash = d.saved !== null && mode === 'new' && from !== d.saved.valid_from && d.versions.some((v) => v.valid_from === from)
  const save = async (): Promise<void> => {
    if (mode === 'new' && d.saved !== null && !/^\d{4}-\d{2}-\d{2}$/.test(from)) { setErr('Укажите дату, с которой действует новый расчёт.'); return }
    if (problems.length > 0) { setErr(problems.join('. ') + '.'); return }
    setBusy(true); setErr(null)
    const x = await saveCalc(d.nm_id, { inputs, tax: taxIn, valid_from: validFrom })
    setBusy(false)
    if (!x.ok) { setErr(x.error.message); return }
    hapticSuccess(); setDirty(false)
    announce({ kind: 'ok', text: `Сохранено: ${rub(x.data.result.profit)} со штуки, действует ${x.data.since}` })
    onPick(x.data.valid_from)
  }
  const remove = async (): Promise<void> => {
    if (d.saved === null || d.saved.is_first) return
    if (!(await confirmAction(`Удалить расчёт, действующий ${d.saved.since}? Продажи с этой даты будут считаться по предыдущему расчёту.`))) return
    setBusy(true)
    const x = await deleteCalc(d.nm_id, d.saved.valid_from)
    setBusy(false)
    if (!x.ok) { setErr(x.error.message); return }
    hapticSuccess()
    announce({ kind: 'ok', text: 'Расчёт удалён' })
    onPick(null)
  }
  const refill = (): void => {
    const p = toForm(d.prefill.inputs)
    setF({ ...f, ...Object.fromEntries(WB_KEYS.map((k) => [k, p[k]])) })
    setDirty(true)
    announce({ kind: 'info', text: 'Подставлены данные WB из отчётов за 4 недели' })
  }
  const fx = d.prefill.facts
  const head = <>
      <section className="card card-pad calc-head">
        {d.photo !== null && <img className="calc-photo" src={d.photo} alt="" width={56} height={74} />}
        <span className="calc-title">
          <span className="row-title">{d.title}</span>
          <span className="row-meta">{[d.vendor_code, `артикул WB ${d.nm_id}`].filter(Boolean).join(' · ')}</span>
        </span>
      </section>
  </>
  const result = <>
      <section className="card card-pad calc-result" data-testid="calc-result" aria-live="polite">
        <p className="row-meta">Прибыль с 1 проданной штуки{dirty ? ' · не сохранено' : d.saved === null ? ''
          : d.saved.state === 'future' ? ` · начнёт действовать ${d.saved.since}` : d.saved.state === 'past' ? ` · действовал ${d.saved.since}, сейчас действует более новый` : ` · действует ${d.saved.since}`}</p>
        <p className={'calc-profit' + (r.profit < 0 ? ' calc-loss' : '')} data-testid="calc-profit">{rub(r.profit)}</p>
        <p className="body-text">маржа {pct(r.margin_pct)} · рентабельность к себестоимости {pct(r.roi_pct)}</p>
        {d.week !== null && !dirty && (
          <p className="row-meta gap-top-s" data-testid="calc-week">Факт за неделю {d.week.period}: продано {d.week.net_units} шт. → {rub(d.week.profit_rub)} — по отчётам WB, цена и реклама каждого дня</p>
        )}
      </section>
  </>
  const fields = <>
      <Sect title="Цена" />
      <section className="card card-pad">
        <Num id="c-price" label="Цена до скидки" unit="₽" value={f.price_before_discount} onChange={set('price_before_discount')} hint={src('price_before_discount')} disabled={ro} />
        <Num id="c-disc" label="Скидка продавца" unit="%" value={f.seller_discount_pct} onChange={set('seller_discount_pct')} disabled={ro} />
        <p className="calc-sum">Цена продажи <b>{rub(r.price)}</b></p>
      </section>

      <Sect title="Удержания Wildberries" aside="на 1 штуку" />
      <section className="card card-pad">
        <Num id="c-comm" label="Комиссия WB" unit="%" value={f.commission_pct} onChange={set('commission_pct')} hint={src('commission_pct')} disabled={ro} />
        <Num id="c-acq" label="Эквайринг" unit="%" value={f.acquiring_pct} onChange={set('acquiring_pct')} hint={src('acquiring_pct')} disabled={ro} />
        <Num id="c-log" label="Логистика" unit="₽" value={f.logistics_rub} onChange={set('logistics_rub')}
          hint={src('logistics_rub') !== undefined ? `${src('logistics_rub')}, с учётом невыкупов и возвратов` : 'с учётом невыкупов и возвратов'} disabled={ro} />
        <Num id="c-stor" label="Хранение" unit="₽" value={f.storage_rub} onChange={set('storage_rub')} hint={src('storage_rub')} disabled={ro} />
        <Num id="c-acc" label="Приёмка и обработка" unit="₽" value={f.acceptance_rub} onChange={set('acceptance_rub')} hint={src('acceptance_rub')} disabled={ro} />
        <p className="calc-sum">К перечислению за 1 шт. <b>{rub(r.to_transfer)}</b></p>
        {!ro && <button type="button" className="link-inline" data-testid="calc-refill" onClick={refill}>Подставить данные WB заново</button>}
      </section>

      <Sect title="Ваши расходы" aside="на 1 штуку" />
      <section className="card card-pad">
        <Num id="c-cogs" label="Себестоимость (закупка)" unit="₽" value={f.cogs_rub} onChange={set('cogs_rub')} hint={src('cogs_rub')} disabled={ro} />
        <Num id="c-pack" label="Упаковка и маркировка" unit="₽" value={f.packaging_rub} onChange={set('packaging_rub')} disabled={ro} />
        <Num id="c-inb" label="Доставка до склада" unit="₽" value={f.inbound_rub} onChange={set('inbound_rub')} disabled={ro} />
        <Num id="c-ads" label="Реклама, доля от цены" unit="%" value={f.ads_pct} onChange={set('ads_pct')} hint={src('ads_pct')} disabled={ro} />
        <Num id="c-other" label="Прочие расходы" unit="₽" value={f.other_rub} onChange={set('other_rub')} disabled={ro} />
      </section>

      <Sect title="НДС" aside="ставка общая для магазина" />
      <section className="card card-pad">
        <div className="calc-field">
          <label htmlFor="c-vat" className="calc-label">Ставка НДС<span className="row-meta calc-hint">НДС входит в цену продажи</span></label>
          <select id="c-vat" className="inp inp-wide" value={vat} disabled={ro} data-testid="c-vat" onChange={(e) => { setVat(e.target.value); setDirty(true) }}>
            {d.vat_rates.map((v) => <option key={v} value={String(v)}>{v === 0 ? 'не плачу' : `${v} %`}</option>)}
          </select>
        </div>
        {vatN > 0 && (
          <div className="calc-break gap-top-s" data-testid="calc-vat">
            <p className="calc-row"><span>НДС с продажи ({vatN}/{100 + vatN} от {rub(r.price)})</span><span>{rub(r.vat_sale)}</span></p>
            {vatN >= 10 && <>
              <p className="calc-row"><span>{MINUS} НДС в себестоимости, упаковке и доставке</span><span>{rub(r.vat_in_cogs)}</span></p>
              <p className="calc-row"><span>{MINUS} НДС в услугах WB</span><span>{rub(r.vat_in_wb)}</span></p>
              {r.vat_in_ads !== 0 && <p className="calc-row"><span>{MINUS} НДС в рекламе</span><span>{rub(r.vat_in_ads)}</span></p>}
            </>}
            <p className="calc-row calc-row-sum"><span>{r.vat < 0 ? 'НДС к возмещению' : 'НДС к уплате'}</span><span>{rub(Math.abs(r.vat))}</span></p>
          </div>
        )}
        {vatN >= 10 && <p className="row-meta gap-top-s">Вычет — {vatN}/{100 + vatN} от расходов. Услуги WB — комиссия, логистика, хранение и приёмка; эквайринг НДС не облагается.</p>}
        {vatN > 0 && vatN < 10 && <p className="row-meta gap-top-s">При ставке {vatN} % входящий НДС к вычету не принимается — платится весь НДС с продажи.</p>}
        <p className="row-meta gap-top-s">Налог на доход (УСН и другие) калькулятор не считает — прибыль показана до него.</p>
        {!d.tax_is_set && <p className="calc-warn" data-testid="calc-tax-warn">Ставка НДС магазина ещё не задана — проверьте её.</p>}
      </section>
  </>
  const breakdown = <>
      <Sect title="Как считается" />
      <section className="card card-pad calc-break" data-testid="calc-break">
        {([
          ['Цена продажи', r.price, false],
          ['Комиссия WB', -r.commission, true], ['Эквайринг', -r.acquiring, true], ['Логистика', -r.logistics, true],
          ['Хранение', -r.storage, true], ['Приёмка и обработка', -r.acceptance, true],
          ['К перечислению', r.to_transfer, false],
          ['Себестоимость, упаковка, доставка', -r.cogs_total, true], ['Реклама', -r.ads, true], ['Прочие расходы', -r.other, true],
          [r.vat < 0 ? 'НДС к возмещению' : 'НДС к уплате', -r.vat, true],
          ['Прибыль с 1 шт.', r.profit, false],
        ] as Array<[string, number, boolean]>).filter(([, v, minus]) => !minus || v !== 0).map(([label, v, minus]) => (
          <p key={label} className={minus ? 'calc-row' : 'calc-row calc-row-sum'}><span>{label}</span><span>{rub(v)}</span></p>
        ))}
      </section>
      <p className="hint">
        {fx.net_sold > 0 ? `За ${fx.window} продано ${fx.sold} шт., возвратов ${fx.returned}${fx.buyout_pct !== null ? `, выкуп ${pct(fx.buyout_pct)}` : ''}. ` : 'Продаж за 4 недели нет — поля WB заполните сами. '}
        {fx.penalties_per_unit !== null && fx.penalties_per_unit > 0
          ? `Штрафы WB за это время — ${rub(fx.penalties_per_unit)} на штуку: как и в калькуляторе WB, сюда они не входят, Штаб вычитает их из прибыли недели отдельно.`
          : 'Штрафы WB, как и в калькуляторе WB, сюда не входят — Штаб вычитает их из прибыли недели отдельно.'}
      </p>
  </>
  const saving = <>
      {!ro && d.saved === null && (
        <p className="hint" data-testid="calc-first">Первый расчёт действует на все продажи — и прошлые, и будущие. Приедет партия с другой себестоимостью —
          сохраните новый расчёт с даты её прихода: прежние продажи останутся по прежнему.</p>
      )}
      {!ro && d.saved !== null && <>
        <Sect title="С какой даты действует" />
        <section className="card card-pad calc-when" data-testid="calc-when">
          <label className="calc-radio">
            <input type="radio" name="calc-mode" checked={mode === 'new'} data-testid="calc-mode-new" onChange={() => setMode('new')} />
            <span>Новая партия или новые условия — с даты</span>
          </label>
          {mode === 'new' && <>
            <input type="date" className="inp inp-wide calc-date" value={from} min="2020-01-01" max={plusDays(d.today, 366)} data-testid="calc-from"
              aria-label="Дата начала действия" onChange={(e) => setFrom(e.target.value)} />
            {/^\d{4}-\d{2}-\d{2}$/.test(from) && (
              <p className="row-meta">Продажи до {dayMonth(from)} останутся по прежнему расчёту, с {dayMonth(from)} — по этому.</p>
            )}
            {clash && <p className="calc-warn" data-testid="calc-clash">С {dayMonth(from)} уже есть расчёт — он будет заменён этим.</p>}
          </>}
          <label className="calc-radio">
            <input type="radio" name="calc-mode" checked={mode === 'fix'} data-testid="calc-mode-fix" onChange={() => setMode('fix')} />
            <span>Исправить открытый расчёт — {d.saved.since}</span>
          </label>
          {mode === 'fix' && <p className="row-meta">Продажи, посчитанные по нему, пересчитаются по исправленному.</p>}
        </section>
      </>}

      {err !== null && <InlineError text={err} testid="calc-err" />}
      {!ro && (
        <div className="form-actions">
          <button type="button" className="btn btn-block" disabled={busy} data-testid="calc-save" onClick={() => { void save() }}>
            {busy ? 'Сохраняю…' : d.saved === null ? 'Сохранить расчёт' : mode === 'fix' ? 'Сохранить исправление' : `Сохранить с ${dayMonth(from)}`}
          </button>
          {d.saved !== null && !d.saved.is_first && (
            <button type="button" className="btn btn-soft btn-block gap-top-s" disabled={busy} data-testid="calc-delete" onClick={() => { void remove() }}>
              <Icon name="action-decline" size={20} />Удалить этот расчёт
            </button>
          )}
        </div>
      )}
      {ro && <p className="hint">Менять расчёт может владелец магазина.</p>}
  </>
  const versions = <>
      {d.versions.length > 0 && <>
        <Sect title="Расчёты по датам" />
        <section className="card" data-testid="calc-versions">
          {d.versions.map((v) => {
            const shown = v.valid_from === d.saved?.valid_from
            return (
              <button type="button" className={'row row-tap' + (shown ? ' row-on' : '')} key={v.valid_from} data-testid={'calc-ver-' + v.valid_from}
                aria-current={shown ? 'true' : undefined} onClick={() => onPick(v.state === 'current' ? null : v.valid_from)}>
                <span className="row-main">
                  <span className="row-title">{capitalize(v.since)}{v.state === 'current' ? ' · действует сейчас' : v.state === 'future' ? ' · начнёт действовать' : ''}</span>
                  <span className="row-meta">себестоимость {rub(v.cogs_rub)} · маржа {pct(v.margin_pct)}{shown ? ' · открыт' : ''}</span>
                </span>
                <span className="row-val"><span className="num">{rub(v.profit_rub)}</span><span className="num-sub">со штуки</span></span>
              </button>
            )
          })}
        </section>
        <button type="button" className="link-inline gap-top-s" data-testid="calc-period" onClick={() => open('S-44')}>Сколько заработали за период →</button>
      </>}
  </>
  // Широкое окно (компьютер): слева поля, справа итог, разбор, сохранение и расчёты по датам — правая
  // колонка липкая и прокручивается сама, итог виден, пока заполняются поля ниже.
  return (
    <div data-testid="screen-body" className="calc">
      {two
        ? <div className="cols cols-calc"><div className="col">{head}{fields}</div><div className="col col-sticky">{result}{breakdown}{saving}{versions}</div></div>
        : <>{head}{result}{fields}{breakdown}{saving}{versions}</>}

      <div className="pager">
        <button type="button" className="btn btn-soft" disabled={d.prev_nm === null} onClick={() => d.prev_nm !== null && replace('S-27', String(d.prev_nm))}>
          <Icon name="nav-back" size={20} />Предыдущий
        </button>
        <button type="button" className="btn btn-soft" disabled={d.next_nm === null} onClick={() => d.next_nm !== null && replace('S-27', String(d.next_nm))}>
          Следующий<Icon name="nav-next" size={20} />
        </button>
      </div>
    </div>
  )
}
